import React, { useEffect, useState } from 'react';
import { checkBackendHealth } from '../api/brapi';
import { formatTradingDate } from '../utils/dates';
import DismissibleNotice from './DismissibleNotice';

export default function MarketDataNotice({ marketData, isAdmin = false }) {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (!marketData || !isAdmin) return undefined;
    let active = true;
    checkBackendHealth()
      .then(data => {
        if (active) setHealth(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [marketData, isAdmin]);

  if (!marketData || !isAdmin) return null;
  const coverage = marketData.coverage || {};
  const averageBars = Number.isFinite(Number(health?.average_bars))
    ? Number(health.average_bars)
    : Number(coverage.averageBars ?? coverage.bars ?? 0);
  const minimumBars = Number.isFinite(Number(health?.min_bars))
    ? Number(health.min_bars)
    : Number(coverage.minBars ?? coverage.bars ?? 0);
  const minimumTicker = health?.min_bars_ticker || coverage.minBarsTicker || null;
  const requiredBars = Number(coverage.requiredBars ?? 520);
  const lastDate = health?.last_date || coverage.lastDate;
  const universeCount = Number(health?.universe_count ?? coverage.universeCount ?? 0);
  const collectedCount = Number(health?.assets_count ?? coverage.activeTickers ?? universeCount);
  const budget = health?.brapi_budget;
  const latest = lastDate ? formatTradingDate(lastDate) : 'indisponível';
  const incomplete = health?.history_coverage?.incomplete || coverage.incomplete || [];
  const recentIpos = health?.history_coverage?.recentIpos || coverage.recentIpos || [];
  const incompleteText = incomplete
    .map(asset => `${asset.ticker} ${asset.bars}${asset.reason ? ` — ${asset.reason}` : ''}`)
    .join(', ');
  const recentIpoText = recentIpos
    .map(asset => `${asset.ticker} ${asset.bars}`)
    .join(', ');
  const eventKey = JSON.stringify({
    source: marketData.source,
    timeframe: marketData.timeframe,
    incomplete: (coverage.incomplete || []).map(asset => [asset.ticker, asset.reason]),
    recentIpos: (coverage.recentIpos || []).map(asset => asset.ticker),
  });

  return (
    <DismissibleNotice
      noticeKey="market-data"
      eventKey={eventKey}
      closeLabel="Fechar aviso de dados"
      data-testid="market-data-notice"
      style={{
        margin: '10px 0',
         padding: '8px 38px 8px 12px',
        border: '1px solid #24324b',
        borderRadius: 8,
        background: '#101a2b',
        color: '#b8c7df',
        fontSize: 12,
        lineHeight: 1.45,
      }}
    >
      <strong style={{ color: '#e4ecfb' }}>Dados: {marketData.source} · {marketData.timeframe}</strong>
      <span>
        {' '}· último dado: {latest}
        {' '}· cobertura média: {averageBars.toFixed(1).replace('.', ',')} candles
        {' '}· mínimo: {minimumBars}{minimumTicker ? ` (${minimumTicker})` : ''}/{requiredBars}
        {universeCount > 0 && ` · universo: ${collectedCount}/${universeCount} ativos`}
      </span>
      {budget && (
        <div>
          BRAPI: {budget.beforePerDay} chamadas/dia antes · {budget.recommendedPerDay} após expansão
          {' '}({budget.policy === 'intraday-baseline-and-open-positions'
            ? 'small caps somente no fechamento diário'
            : 'universo completo intraday'})
          {budget.fitsPlan === true ? ' · dentro do plano' : ''}
          {budget.fitsPlan === false ? ' · acima do limite configurado' : ''}
        </div>
      )}
      {incomplete.length > 0 && (
        <div role="alert" style={{ color: '#f4be65' }}>
          Dados incompletos: {incompleteText}. Indicadores de longo prazo podem estar indisponíveis.
        </div>
      )}
      {recentIpos.length > 0 && (
        <div role="status" style={{ color: '#9fb0c9' }}>
          {recentIpos.length} {recentIpos.length === 1 ? 'ativo' : 'ativos'} com histórico curto por IPO recente
          {' '}({recentIpoText}) — indicadores de longo prazo indisponíveis para eles.
        </div>
      )}
    </DismissibleNotice>
  );
}